// Client side of public.ptw_command. The browser sends business input, an
// idempotency key and the row version it last saw; the server derives who and when.
import type { PostgrestError } from '@supabase/supabase-js'
import type { Blocker, CommandArgs, CommandName, CommandResult } from '@/domain/types'
import { isAccountBuild } from './env'
import { inactiveAccountState } from './identity'
import { requireActiveClient, SignInExpiredError } from './session'

const MESSAGES: Record<string, string> = {
  PW001: 'This step cannot be completed yet.',
  PW002: 'Someone else changed this permit. Refresh to see the latest before trying again.',
  PW003: 'This request was already sent with different details. Start the step again.',
  PW004: isAccountBuild
    ? 'Your account is not allowed to do this. Ask your account administrator if you need this access.'
    : 'Your session is not allowed to do this. Switch persona or sign in again.',
  PW005: 'Not found, or not visible to you.',
  PW006: 'Some information is missing or not valid.',
  PW007: 'Signed records cannot be changed. Add a new record instead.',
  PW008: 'This environment does not allow that action.',
  PW009: 'This step is not built in the first release.',
  NETWORK: 'Not confirmed by the server.',
}

export class PtwError extends Error {
  code: string
  blockers: Blocker[]
  detail: unknown
  constructor(code: string, message: string, detail: unknown) {
    super(message)
    this.code = code
    this.detail = detail
    this.blockers = Array.isArray(detail) ? (detail as Blocker[]) : []
  }

  /**
   * The sign-in here has ended: the hour ran out (or the server refused the expired token),
   * or - demo only - the persona was signed in on another device (the server disabled this
   * device's account: PW004 ACCOUNT_INACTIVE). A real account that is suspended or disabled
   * is not a sign-in problem: signing in again would not help (accountInactive).
   */
  get needsSignInAgain(): boolean {
    if (this.code === 'SIGN_IN_EXPIRED') return true
    return !isAccountBuild && this.code === 'PW004' && /ACCOUNT_INACTIVE/.test(this.message)
  }

  /** Account builds: the server says this account is suspended, disabled or missing. */
  get accountInactive(): 'suspended' | 'disabled' | 'none' | null {
    if (!isAccountBuild || this.code !== 'PW004' || !/ACCOUNT_INACTIVE/.test(this.message)) return null
    return inactiveAccountState(this.detail) ?? 'none'
  }

  /** Friendly one-line summary for banners. */
  get summary(): string {
    const inactive = this.accountInactive
    if (inactive === 'suspended') return 'Your account is suspended. Ask your account administrator to reactivate it.'
    if (inactive === 'disabled') return 'Your account is disabled. Ask your account administrator.'
    if (inactive === 'none') return 'This sign-in has no account. Sign out and sign in with your invited email address.'
    if (this.code === 'PW004' && /ACCOUNT_INACTIVE/.test(this.message)) {
      return 'This persona was signed in on another device, which ended its sign-in here. Sign in again here to continue (that ends it on the other device).'
    }
    if (this.code === 'SIGN_IN_EXPIRED') return 'Your sign-in has ended. Sign in again here to continue.'
    return MESSAGES[this.code] ?? this.message
  }

  get field(): string | null {
    const d = this.detail as { field?: string } | null
    return d && typeof d === 'object' && 'field' in d ? (d.field ?? null) : null
  }

  get problem(): string | null {
    const d = this.detail as { problem?: string } | null
    return d && typeof d === 'object' && 'problem' in d ? (d.problem ?? null) : null
  }

  static from(error: PostgrestError | Error | unknown): PtwError {
    if (error instanceof PtwError) return error
    if (error instanceof SignInExpiredError) return new PtwError('SIGN_IN_EXPIRED', error.message, null)
    const e = error as Partial<PostgrestError> & { message?: string }
    let detail: unknown = e.details ?? null
    if (typeof e.details === 'string') {
      try {
        detail = JSON.parse(e.details)
      } catch {
        detail = e.details
      }
    }
    // PostgREST refuses a token past the project's one-hour session limit (JWT expired).
    if (e.code === 'PGRST301' || /JWT expired/i.test(e.message ?? '')) {
      return new PtwError('SIGN_IN_EXPIRED', 'Your sign-in has ended. Sign in again to continue.', null)
    }
    const code = typeof e.code === 'string' && e.code ? e.code : 'NETWORK'
    const message =
      code === 'NETWORK'
        ? 'The request did not reach the server. Nothing was recorded unless a refresh shows it. Stop work physically if needed and try again.'
        : (e.message ?? 'Unexpected error')
    return new PtwError(code, message, detail)
  }
}

export type RunOptions = {
  expectedRowVersion?: number | null
  /** Reuse the same key when retrying the same intent after an ambiguous failure. */
  idempotencyKey?: string
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

export async function runCommand<C extends CommandName>(
  name: C,
  args: CommandArgs[C],
  opts: RunOptions = {},
): Promise<CommandResult> {
  const client = requireActiveClient()
  const { data, error } = await client.rpc('ptw_command', {
    p_command: name,
    p_args: args,
    p_idempotency_key: opts.idempotencyKey ?? newIdempotencyKey(),
    p_expected_row_version: opts.expectedRowVersion ?? null,
  })
  if (error) throw PtwError.from(error)
  return data as CommandResult
}

export async function callRead<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const client = requireActiveClient()
  const { data, error } = await client.rpc(fn, args)
  if (error) throw PtwError.from(error)
  return data as T
}
